import React, { useEffect, useMemo } from 'react';
import { WorkflowIcon, BellRing, type LucideIcon } from 'lucide-react';
import type { NodeStruct } from '@/types/node-types';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useOrgStore } from '@/stores/org-store';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { ROUTES } from '@/constants/routes';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * On-canvas capability chips for AgentNodes.
 *
 * An agent node can carry runtime-injected capabilities that are invisible
 * on the canvas today: workflow-launch tools (scoped `run_<alias>` tools
 * from the structured `delegable_workflows` param, or the open-ended
 * workflow-runtime toolset when a payload carries
 * `enable_workflow_runtime: true`) and mid-run notifications
 * (`enable_notifications`). These chips surface them in the node's tool
 * area, using the same tile + label grammar as the connected-tool slot
 * rows (ConnectedNodeStatus).
 *
 * Detection is static from the node config only — prompt text is never
 * parsed. `enable_workflow_runtime` defaults ON server-side, but absent
 * an explicit param there is nothing config-level to show, so no chip
 * renders for plain agent nodes.
 */

/** Violet: existing canvas vocabulary for workflow control flow. */
const LAUNCHER_COLOR = '#8B5CF6';
/** Amber: existing canvas vocabulary for attention/paused states. */
const NOTIFICATIONS_COLOR = '#F59E0B';

export const AGENT_NODE_ID = 'agent_node';
export const DELEGABLE_WORKFLOWS_FIELD_ID = 'delegable_workflows';

/**
 * One entry of the structured `delegable_workflows` param, mirroring the
 * backend contract in nodes/agent/delegable_workflows.py. Shared with the
 * sidebar Launches editor (NodeLaunchesSection), which imports this shape
 * and the parser below.
 */
export interface DelegableWorkflowEntry {
  workflow_id: string;
  version_id?: string | null;
  alias: string;
  when_to_use: string;
  launch_budget?: string | null;
}

/**
 * Parse the stored param value (entry array, or the JSON string the
 * editor serializes) into entries. Returns null when the value is
 * present but unparseable — callers treat that as invalid config.
 */
export function parseDelegableWorkflowsValue(
  value: unknown,
): DelegableWorkflowEntry[] | null {
  if (value === undefined || value === null || value === '') return [];
  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(parsed)) return null;
  const entries: DelegableWorkflowEntry[] = [];
  for (const raw of parsed) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return null;
    }
    const item = raw as Record<string, unknown>;
    entries.push({
      workflow_id: String(item.workflow_id ?? ''),
      version_id:
        item.version_id === undefined || item.version_id === null
          ? null
          : String(item.version_id),
      alias: String(item.alias ?? ''),
      when_to_use: String(item.when_to_use ?? ''),
      launch_budget:
        item.launch_budget === undefined || item.launch_budget === null
          ? null
          : String(item.launch_budget),
    });
  }
  return entries;
}

interface AgentCapabilityChipsProps {
  struct: NodeStruct;
}

function isFlagOn(value: unknown): boolean {
  return value === true || value === 'true';
}

function getParamValue(struct: NodeStruct, fieldId: string): unknown {
  return struct.params?.find((param) => param.field_id === fieldId)?.value;
}

/** Same 32px tile as IconTile/ConnectedNodeStatus, taking a Lucide component. */
const ChipTile: React.FC<{ icon: LucideIcon; color: string }> = ({
  icon: Icon,
  color,
}) => (
  <div
    className='flex items-center justify-center rounded-lg shrink-0 w-8 h-8'
    style={{
      background: `linear-gradient(to bottom, ${color}, color-mix(in srgb, ${color} 90%, black) )`,
    }}
  >
    <Icon className='w-5! h-5!' style={{ color: '#ffffff' }} />
  </div>
);

interface CapabilityChipProps {
  icon: LucideIcon;
  color: string;
  label: string;
  tooltip?: string;
  testId: string;
  onClick?: () => void;
}

/** One chip row, mirroring the slot-card row's visual grammar. */
const CapabilityChip: React.FC<CapabilityChipProps> = ({
  icon,
  color,
  label,
  tooltip,
  testId,
  onClick,
}) => {
  const row = (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      data-testid={testId}
      className={`nodrag flex items-center w-full gap-1 pl-0.5 py-1 mb-[6px] rounded-xl relative transition-colors duration-200 bg-muted-background border-2 border-transparent ${
        onClick
          ? 'cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-600'
          : ''
      }`}
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onClick();
              }
            }
          : undefined
      }
    >
      <ChipTile icon={icon} color={color} />
      <TruncateTextWithTooltip
        text={label}
        className='text-sm font-medium pl-1 min-w-0 dark:text-white!'
      />
    </div>
  );

  if (!tooltip) return row;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{row}</TooltipTrigger>
      <TooltipContent side='right' align='start' className='max-w-xs'>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

export const AgentCapabilityChips: React.FC<AgentCapabilityChipsProps> = ({
  struct,
}) => {
  const navigate = useOrgNavigate();
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const savedWorkflows = useWorkflowStore((state) => state.savedWorkflows);
  const templateWorkflows = useWorkflowStore(
    (state) => state.templateWorkflows,
  );

  const isAgentNode = struct.node_id === AGENT_NODE_ID;

  // Structured launch targets. Malformed JSON renders the
  // generic capability chip rather than nothing: the runtime tools are
  // still configured, and hiding them is exactly the invisibility this
  // component exists to fix.
  const delegableValue = isAgentNode
    ? getParamValue(struct, DELEGABLE_WORKFLOWS_FIELD_ID)
    : undefined;
  const parsedEntries = useMemo(
    () => parseDelegableWorkflowsValue(delegableValue),
    [delegableValue],
  );
  const entries = useMemo(
    () =>
      (parsedEntries ?? []).filter(
        (entry): entry is DelegableWorkflowEntry => !!entry.workflow_id,
      ),
    [parsedEntries],
  );
  const delegableMalformed =
    parsedEntries === null && delegableValue !== undefined;

  const workflowNamesById = useMemo(() => {
    const map = new Map<string, string>();
    for (const workflow of [...savedWorkflows, ...templateWorkflows]) {
      map.set(workflow.workflow_id, workflow.name);
    }
    return map;
  }, [savedWorkflows, templateWorkflows]);

  // The dashboard populates the store's workflow list, but a direct
  // editor-URL load doesn't — lazily load it (once; isLoadingWorkflows
  // gates concurrent chip instances) so target names resolve instead
  // of falling back to aliases.
  const needsNames =
    entries.length > 0 &&
    entries.some((entry) => !workflowNamesById.has(entry.workflow_id));
  useEffect(() => {
    if (!isAgentNode || !needsNames || !orgId) return;
    const { savedWorkflows: loaded, isLoadingWorkflows } =
      useWorkflowStore.getState();
    if (loaded.length > 0 || isLoadingWorkflows) return;
    useWorkflowStore
      .getState()
      .getWorkflowList(orgId)
      .catch(() => {
        // Name resolution is best-effort; chips fall back to aliases.
      });
  }, [isAgentNode, needsNames, orgId]);

  if (!isAgentNode) return null;

  // Open-ended runtime launch tools: only when the payload carries the
  // flag explicitly (legacy schemas / agent-runtime canvases).
  const hasRuntimeFlag = isFlagOn(
    getParamValue(struct, 'enable_workflow_runtime'),
  );
  const hasNotifications = isFlagOn(
    getParamValue(struct, 'enable_notifications'),
  );

  const showGenericLauncher =
    entries.length === 0 && (hasRuntimeFlag || delegableMalformed);

  if (entries.length === 0 && !showGenericLauncher && !hasNotifications) {
    return null;
  }

  return (
    <div className='px-4 relative'>
      {entries.map((entry) => {
        const name =
          workflowNamesById.get(entry.workflow_id) ||
          entry.alias ||
          entry.workflow_id;
        return (
          <CapabilityChip
            key={`${entry.workflow_id}-${entry.alias}`}
            icon={WorkflowIcon}
            color={LAUNCHER_COLOR}
            label={`launches → ${name}`}
            tooltip={
              entry.when_to_use
                ? `run_${entry.alias}: ${entry.when_to_use}`
                : `Launches workflow via run_${entry.alias}`
            }
            testId={`capability-chip-launch-${entry.alias}`}
            onClick={() =>
              navigate(`${ROUTES.WORKFLOW.ROOT}/${entry.workflow_id}`)
            }
          />
        );
      })}
      {showGenericLauncher && (
        <CapabilityChip
          icon={WorkflowIcon}
          color={LAUNCHER_COLOR}
          label='Workflow launcher'
          tooltip='This agent can launch workflows at runtime — targets defined in system prompt'
          testId='capability-chip-launcher'
        />
      )}
      {hasNotifications && (
        <CapabilityChip
          icon={BellRing}
          color={NOTIFICATIONS_COLOR}
          label='Notifications'
          tooltip='This agent can send mid-run user notifications (notify_user)'
          testId='capability-chip-notifications'
        />
      )}
    </div>
  );
};
